import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

// Modal/Popup state types
export const PopupType = {
  NONE: "",
  ACTIVATE_CUSTOMER: "activate_customer",
  SUSPEND_CUSTOMER: "suspend_customer",
  DELETE_STAFF: "delete_staff",
  SUSPEND_STAFF: "suspend_staff",
  REACTIVATE_STAFF: "reactivate_staff",
  CONFIRM_ACTION: "confirm_action",
} as const;

export type PopupType = (typeof PopupType)[keyof typeof PopupType];

export interface PopupState {
  isOpen: PopupType;
  id: string;
  action?: string;
  title?: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface DrawerState {
  customerFormOpen: boolean;
  staffFormOpen: boolean;
  currentId: string | null;
}

export interface HeaderState {
  showBackButton: boolean;
  backButtonText: string;
  backRoute: string | null;
}

export interface UIState {
  popup: PopupState;
  drawer: DrawerState;
  header: HeaderState;
  loading: {
    global: boolean;
    customers: boolean;
    staff: boolean;
  };
}

const initialState: UIState = {
  popup: {
    isOpen: PopupType.NONE,
    id: "",
    action: "",
    title: "",
    message: "",
    data: {},
  },
  drawer: {
    customerFormOpen: false,
    staffFormOpen: false,
    currentId: null,
  },
  header: {
    showBackButton: false,
    backButtonText: "Back",
    backRoute: null,
  },
  loading: {
    global: false,
    customers: false,
    staff: false,
  },
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    // Popup actions
    openPopup: (state, { payload }: PayloadAction<PopupState>) => {
      state.popup = payload;
    },
    closePopup: (state) => {
      state.popup = initialState.popup;
    },

    // Drawer actions
    openCustomerForm: (state, { payload }: PayloadAction<string | null>) => {
      state.drawer.customerFormOpen = true;
      state.drawer.currentId = payload;
    },
    closeCustomerForm: (state) => {
      state.drawer.customerFormOpen = false;
      state.drawer.currentId = null;
    },
    openStaffForm: (state, { payload }: PayloadAction<string | null>) => {
      state.drawer.staffFormOpen = true;
      state.drawer.currentId = payload;
    },
    closeStaffForm: (state) => {
      state.drawer.staffFormOpen = false;
      state.drawer.currentId = null;
    },
    closeAllDrawers: (state) => {
      state.drawer = initialState.drawer;
    },

    // Loading actions
    setGlobalLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading.global = payload;
    },
    setCustomersLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading.customers = payload;
    },
    setStaffLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading.staff = payload;
    },

    // Header actions
    setHeaderProps: (
      state,
      {
        payload,
      }: PayloadAction<
        Partial<Omit<HeaderState, "onBackClick">> & {
          onBackClick?: (() => void) | null;
        }
      >,
    ) => {
      state.header = {
        ...state.header,
        ...payload,
      };
    },
    clearHeaderProps: (state) => {
      state.header = initialState.header;
    },

    // Reset UI state
    resetUI: () => initialState,
  },
});

export const {
  openPopup,
  closePopup,
  openCustomerForm,
  closeCustomerForm,
  openStaffForm,
  closeStaffForm,
  closeAllDrawers,
  setGlobalLoading,
  setCustomersLoading,
  setStaffLoading,
  setHeaderProps,
  clearHeaderProps,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer;
